import React, { useState, useCallback } from 'react';
import styled from 'styled-components';

// Production-safe download button with robust error handling
const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${props => props.disabled ? '#e2e8f0' : 'linear-gradient(135deg, #4299e1 0%, #3182ce 100%)'};
  color: ${props => props.disabled ? '#a0aec0' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #3182ce 0%, #2c5282 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 0.5rem;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${props => props.progress}%;
    background-color: rgba(255, 255, 255, 0.8);
    transition: width 0.3s ease;
  }
`;

const ErrorMessage = styled.div`
  color: #e53e3e;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #fed7d7;
  border-radius: 4px;
  border-left: 3px solid #e53e3e;
`;

const SuccessMessage = styled.div`
  color: #38a169;
  font-size: 0.8rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #c6f6d5;
  border-radius: 4px;
  border-left: 3px solid #38a169;
`;

// Production-safe download button with comprehensive error handling
const DownloadButton = ({
  category = 'unknown',
  filename = '',
  caption = 'Image',
  downloadUrl = null,
  className = '',
  onError = null,
  style = {},
  ...props
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Comprehensive URL generation with multiple fallbacks
  const generateDownloadUrls = useCallback(() => {
    // If explicit downloadUrl is provided, use it first
    if (downloadUrl) {
      return [downloadUrl];
    }

    if (!filename) {
      throw new Error('No filename provided for download');
    }

    // Generate fallback URLs based on the existing image infrastructure
    const urls = [];

    // Clean category name for URL construction
    const cleanCategory = category?.toLowerCase().replace(/[^a-z0-9-]/g, '-') || 'general';

    // Primary: Try S3 CloudFront CDN (fastest)
    urls.push(`https://d38eae84m60k6u.cloudfront.net/hospital-journey/${filename}`);

    // Secondary: Try direct S3 bucket
    urls.push(`https://maggies-miracle-media-2025-ssoward.s3.amazonaws.com/hospital-journey/${filename}`);

    // Tertiary: Try local server path based on category
    if (cleanCategory.includes('hospital') || cleanCategory.includes('journey')) {
      urls.push(`/uploads/hospital-journey/optimized/${filename}`);
    } else if (cleanCategory.includes('award') || cleanCategory.includes('eliza')) {
      urls.push(`/uploads/elizas-award/ceremony/${filename}`);
      urls.push(`/uploads/elizas-award/first-responders/${filename}`);
    } else {
      urls.push(`/uploads/${cleanCategory}/${filename}`);
      urls.push(`/uploads/general/${filename}`);
    }

    return urls;
  }, [category, filename, downloadUrl]);

  // Robust download function with multiple retry strategies
  const handleDownload = useCallback(async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    setProgress(0);
    setError(null);
    setSuccess(false);

    try {
      const urls = generateDownloadUrls();
      let lastError = null;

      // Try each URL in sequence
      for (let i = 0; i < urls.length; i++) {
        const url = urls[i];
        setProgress((i / urls.length) * 50); // First 50% is trying URLs

        try {
          console.log(`Attempting download from: ${url}`);

          // Fetch with timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

          const response = await fetch(url, {
            signal: controller.signal,
            headers: {
              'Accept': 'image/*,*/*',
              'Cache-Control': 'no-cache'
            }
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          setProgress(75); // 75% when fetch succeeds

          // Get blob and create download
          const blob = await response.blob();
          setProgress(90); // 90% when blob ready

          // Create download link
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = filename || `download-${Date.now()}.jpg`;

          // Trigger download
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Cleanup
          window.URL.revokeObjectURL(downloadUrl);

          setProgress(100);
          setSuccess(true);

          // Track successful download
          if (window.gtag && typeof window.gtag === 'function') {
            window.gtag('event', 'download', {
              'file_name': filename,
              'file_type': 'image',
              'category': category,
              'method': `url_${i + 1}`
            });
          }

          console.log(`✅ Download successful from: ${url}`);
          return; // Success, exit the function

        } catch (err) {
          console.warn(`❌ Download failed from ${url}:`, err.message);
          lastError = err;
          continue; // Try next URL
        }
      }

      // If we get here, all URLs failed
      throw new Error(`Download failed from all sources. Last error: ${lastError?.message || 'Unknown error'}`);

    } catch (err) {
      console.error('Download error:', err);
      setError(`Download failed: ${err.message}`);

      // Call optional error handler
      if (onError && typeof onError === 'function') {
        onError(err);
      }

      // Track failed download
      if (window.gtag && typeof window.gtag === 'function') {
        window.gtag('event', 'download_error', {
          'file_name': filename,
          'error_message': err.message,
          'category': category
        });
      }
    } finally {
      setIsDownloading(false);

      // Clear success message after delay
      if (success) {
        setTimeout(() => setSuccess(false), 3000);
      }
    }
  }, [category, filename, generateDownloadUrls, onError, isDownloading, success]);

  // Don't render if no filename
  if (!filename && !downloadUrl) {
    return null;
  }

  return (
    <div className={className} style={style}>
      <Button
        onClick={handleDownload}
        disabled={isDownloading}
        {...props}
      >
        <span role="img" aria-label="download">📥</span>
        {isDownloading ? 'Downloading...' : 'Download'}
      </Button>

      {isDownloading && (
        <ProgressBar progress={progress} />
      )}

      {error && (
        <ErrorMessage>
          {error}
        </ErrorMessage>
      )}

      {success && (
        <SuccessMessage>
          ✅ Downloaded successfully!
        </SuccessMessage>
      )}
    </div>
  );
};

export default DownloadButton;